import Image from "next/image";
import { MDBCol, MDBContainer, MDBModal, MDBRow } from "mdb-react-ui-kit";
import SwiperClass from "swiper";
import { FC, useCallback, useEffect, useState } from "react";
import styles from "../../styles/detail/DetailHotelGallery.module.scss";
import packageDetailCustomSvg from "../../utils/svg-icon/detailPackageCustom";
import userInterfaceSvg from "../../utils/svg-icon/userInterface";
import { HotelReadableTO } from "../../models/detail";
import { useIsMD } from "../../utils/hooks/useWindowSizes";
import Carousel from "../common/carousel/Carousel";
import ButtonBack from "../common/carousel/ButtonBack";
import ButtonNext from "../common/carousel/ButtonNext";
import { TBreackpointsCarousel } from "../../models/common/common";

const PackageDetailHotelGallery: FC<{
    show: boolean;
    setShow: (el: boolean) => void;
    currentHotel: HotelReadableTO;
    // eslint-disable-next-line sonarjs/cognitive-complexity
}> = ({ show, setShow, currentHotel }) => {
    const [readMore, setReadMore] = useState<boolean>(false);
    const [iconImageError, setIconImageError] = useState<number[]>([]);
    const [swiperClass, setSwiperClass] = useState<SwiperClass | null>(null);

    const { CloseLine, StarFilled } = userInterfaceSvg;
    const { Pointer } = packageDetailCustomSvg;

    const getStars = useCallback(() => {
        return Array.from(
            { length: +(currentHotel?.stars ?? "0") },
            (value, key) => <StarFilled key={key} />
        );
    }, [currentHotel?.stars]);

    const [render, setRender] = useState(false);

    useEffect(() => setRender(true), []);

    const isMD = useIsMD();

    const breackPointCarousel: TBreackpointsCarousel = {
        0: {
            slidesPerView: 1.3,
            height: 533,
            spaceBetween: 12,
        },
        576: {
            slidesPerView: 2,
            height: 533,
            spaceBetween: 12,
        },
        768: {
            slidesPerView: 2.5,
            height: 533,
            spaceBetween: 12,
        },
        992: {
            slidesPerView: 3,
            height: 533,
            spaceBetween: 12,
        },
        1200: {
            slidesPerView: 3,
            spaceBetween: 32,
        },
        1400: {
            slidesPerView: 3,
            spaceBetween: 32,
        },
        1600: {
            slidesPerView: 3,
            spaceBetween: 32,
        },
        1920: {
            slidesPerView: 4,
            spaceBetween: 32,
        },
    };

    return render ? (
        <MDBModal
            className={`${styles.modal} p-5`}
            show={show}
            setShow={setShow}
            staticBackdrop
        >
            <MDBContainer className={styles.galleryContainer}>
                <MDBRow>
                    <MDBCol className="d-flex justify-content-end">
                        <div onClick={() => setShow(false)}>
                            <span className={styles.interactionSvgSpan}>
                                <CloseLine />
                            </span>
                        </div>
                    </MDBCol>
                </MDBRow>
                <MDBRow>
                    <MDBCol className="d-flex justify-content-between mt-5 px-5">
                        <div>
                            {isMD && (
                                <div
                                    className={` ${styles.starContainer} d-flex justify-content-start`}
                                >
                                    {getStars()}
                                </div>
                            )}
                            <h3 className={styles.title}>
                                {currentHotel?.name}
                            </h3>
                            <div className="d-flex align-items-center">
                                <span className={styles.pointerContainer}>
                                    <Pointer />
                                </span>
                                <span className={styles.subtitle}>
                                    {currentHotel?.address}
                                </span>

                                {!isMD && (
                                    <>
                                        <div className={styles.starContainer}>
                                            {getStars()}
                                        </div>
                                        <span className={styles.subtitle}>
                                            stars hotel
                                        </span>
                                    </>
                                )}
                            </div>
                        </div>
                        {!isMD && (
                            <MDBRow className="mt-2">
                                <MDBCol center className="pe-0">
                                    <ButtonBack swiperClass={swiperClass} />
                                </MDBCol>
                                <MDBCol center className="ps-0">
                                    <ButtonNext swiperClass={swiperClass} />
                                </MDBCol>
                            </MDBRow>
                        )}
                    </MDBCol>
                </MDBRow>
                <MDBRow>
                    <MDBCol className="px-5 mt-4" style={{ height: "375px" }}>
                        <Carousel
                            setSwiperClass={setSwiperClass}
                            visibleSlides={3}
                            centerInsufficientSlides={false}
                            breakpoints={breackPointCarousel}
                            classNameCarousel="h-100"
                        >
                            {currentHotel?.gallery.map((el, idx) => (
                                <div
                                    key={el}
                                    className="position-relative h-100"
                                >
                                    <Image
                                        src={
                                            iconImageError.includes(idx)
                                                ? "/images/tours/across-italy-reduced.jpg"
                                                : el
                                        }
                                        alt="hotel picture"
                                        fill
                                        className={styles.galleryImage}
                                        onError={() => {
                                            setIconImageError(
                                                prevImageErrors => [
                                                    ...prevImageErrors,
                                                    idx,
                                                ]
                                            );
                                        }}
                                    />
                                </div>
                            ))}
                        </Carousel>
                    </MDBCol>
                </MDBRow>
                <MDBRow>
                    <MDBCol lg={9} className="px-5">
                        <div className="mt-4">
                            <p
                                className={`${
                                    !readMore && styles.multilineEllipsis
                                }`}
                            >
                                {currentHotel?.description}
                            </p>
                            <span
                                className={`${styles.cardSecondaryText} me-2`}
                            >
                                {readMore ? "Read less " : "Read more"}
                            </span>

                            {readMore ? (
                                <span
                                    onClick={() => setReadMore(!readMore)}
                                    className={styles.spanMoreInfo}
                                >
                                    <userInterfaceSvg.ArrowUpSimpleLine />
                                </span>
                            ) : (
                                <span
                                    onClick={() => setReadMore(!readMore)}
                                    className={styles.spanMoreInfo}
                                >
                                    <userInterfaceSvg.ArrowDownSimpleLine />
                                </span>
                            )}
                        </div>
                    </MDBCol>
                </MDBRow>
            </MDBContainer>
        </MDBModal>
    ) : (
        <div />
    );
};

export default PackageDetailHotelGallery;
